/* eslint-disable @typescript-eslint/no-empty-function */
import { createContext, useContext, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import Subscription from '../../../../domain/models/Subscription';
import SubscriptionSettings from '../../../../domain/models/subscription/SubscriptionSettings';
import updateSubscription from '../../../../domain/use_cases/subscriptions/updateSubscription';
import deleteSubscription from '../../../../domain/use_cases/subscriptions/deleteSubscription';
import { sendStartEmail } from '../../../../domain/use_cases/subscriptions/sendStartEmail';
import SubscriptionOpeningDays from '../../../../domain/enums/subscription/subscription_opening/SubscriptionOpeningDays';
import SubscriptionTimezoneCity from '../../../../domain/enums/subscription/subscription_timezone/SubscriptionTimezoneCity';
import SubscriptionTimezoneContinent from '../../../../domain/enums/subscription/subscription_timezone/SubscriptionTimezoneContinent';
import SubscriptionTimezoneCountry from '../../../../domain/enums/subscription/subscription_timezone/SubscriptionTimezoneCountry';
import AuthenticationContext from '../../auth/AuthenticationContext';
import { message } from 'antd';
import SubscriptionDataSource from '../../../../domain/enums/subscription/subscription_datasource/SubscriptionDataSource';
import SubscriptionPartnerSettings from '../../../../domain/models/subscription/SubscriptionPartnerSettings';
import getPartnerUserUuids from '../../../../domain/use_cases/subscriptions/getPartnerUserUuids';
import terminateSubscriptionUseCase, {
  TerminateSubscriptionFailure,
} from '../../../../domain/use_cases/subscriptions/terminateSubscription';
import resetSubscriptionCreditsUseCase, {
  ResetSubscriptionCreditsFailure,
} from '../../../../domain/use_cases/subscriptions/resetSubscriptionCredits';
import addSubscriptionCreditsUseCase, {
  AddSubscriptionCreditsFailure,
} from '../../../../domain/use_cases/subscriptions/addSubscriptionCredits';
import updateSubscriptionCreditsUseCase, {
  UpdateSubscriptionCreditsFailure,
} from '../../../../domain/use_cases/subscriptions/updateSubscriptionCredits';
import updateCreditsAnchorDayUseCase, {
  UpdateCreditsAnchorDayFailure,
} from '../../../../domain/use_cases/subscriptions/updateCreditsAnchorDay';
import cancelSubscriptionPlanChangeUseCase, {
  CancelSubscriptionPlanChangeFailure,
} from '../../../../domain/use_cases/subscriptions/cancelSubscriptionPlanChange';
import changeSubscriptionPlanUseCase, {
  ChangeSubscriptionPlanFailure,
} from '../../../../domain/use_cases/subscriptions/changeSubscriptionPlan';
import cancelScheduledPlanChangeUseCase, {
  CancelScheduledPlanChangeFailure,
} from '../../../../domain/use_cases/subscriptions/cancelScheduledPlanChange';
import {
  ContextStatus,
  CostTypeRef,
  ECreditAdjustmentReason,
  ECreditType,
  PartnersType,
} from '@leedpulse-monorepo/enums';
import { ISmallUser, ICustomProspectType, INumber, ILeedbucksRatesTemp, ILeedbucksCreditsTemp } from '@leedpulse-monorepo/interfaces';
import getOrishaCustomProspectTypes from '../../../../domain/use_cases/subscriptions/getPartnerCustomProspectTypes';

export interface SubscriptionEditionFormContextProps {
  wipSubscription: Subscription | null;
  savedSubscription: Subscription | null; // last saved version, tells what is not saved yet
  sendersHaveChanged: boolean;
  updateStatus: ContextStatus;
  deleteStatus: ContextStatus;
  partnerStatus: ContextStatus;
  partnerSettingsUserUuids: ISmallUser[];
  customProspectTypesStatus: ContextStatus;
  customProspectTypes: ICustomProspectType[];
  initWithSubscription: (subscription: Subscription) => void;
  submitUpdate: () => Promise<void>;
  submitDelete: () => Promise<void>;
  fetchPartnerUserUuids: () => Promise<void>;
  fetchPartnerCustomProspectTypes: () => Promise<void>;
  onCreditsChange: (type: ECreditType, value: number) => void;
  onClearCredit: (type: ECreditType) => void;
  onResetCredits: (reason: ECreditAdjustmentReason, comment: string | null) => Promise<boolean>;
  onAddCredits: (amount: number, reason: ECreditAdjustmentReason, comment: string | null) => Promise<boolean>;
  onSetCredits: (leedbucks: number, reason: ECreditAdjustmentReason, comment: string | null) => Promise<boolean>;
  onCreditsAnchorDayChange: (day: number) => Promise<void>;
  onCancelPlanChange: (companyId: string) => Promise<void>;
  onChangePlan: (companyId: string, newPlanId: string, scheduledDate: string | null) => Promise<boolean>; // scheduledDate YYYY-MM-DD, null for now
  onCancelScheduledPlanChange: () => Promise<void>;
  onNameChange: (name: string) => void;
  onSubscriptionSettingsEmailNotificationCopyChange: (copies: string[]) => void;
  onSubscriptionSettingsBetaAccountChange: (beta_account: boolean) => void;
  onSubscriptionSettingsDemoAccountChange: (demo_account: boolean) => void;
  onSubscriptionSettingsPaygEnabledChange: (payg_enabled: boolean) => void;
  onSubscriptionSettingsAutoArchiveTasksDaysChange: (tasks_auto_archive_days: number) => void;
  onSubscriptionSettingsPaymentWallChange: (payment_wall: boolean) => void;
  subscriptionStartEmail: (subscriptionsContext: any, aiFullName: string) => Promise<void>;
  onPartnerSettingsImportModeChange: (import_mode: string) => void;
  onPartnerSettingsProspectsSeniorityChange: (prospects_seniority: string) => void;
  onPartnerSettingsProspectsSeniorityMaxChange: (prospects_seniority_max: string) => void;
  onPartnerSettingsIncludeBuyersWithoutIdentifiedSearchChange: (include_buyers_without_identified_search: boolean) => void;
  onPartnerSettingsIncludeSellersWithoutEstimationChange: (include_sellers_without_estimation: boolean) => void;
  onPartnerSettingsIncludeContactsWithoutTypologyChange: (include_contacts_without_typology: boolean) => void;
  onPartnerSettingsIncludeArchivedChange: (include_archived: boolean) => void;
  onPartnerSettingsArchiveProspectsWithoutInterestChange: (archive_prospects_without_interest: boolean) => void;
  onPartnerSettingsMaxCreditConsumptionPerMonthChange: (max_credit_consumption_per_month: number) => void;
  onPartnerSettingsProspectsTypologiesChange: (prospects_typologies: string[]) => void;
  onPartnerSettingsProspectsTypologiesToScenariosChange: (prospects_typology: string, scenarioId: string) => void;
  onPartnerSettingsUserUuidsChange: (users_uuids: string[]) => void;
  onIdPlanChange: (idPlan: string) => void;
  onOpeningDaysChange: (openingDays: SubscriptionOpeningDays) => void;
  onOpeningHoursStartChange: (hours: number) => void;
  onOpeningHoursEndChange: (hours: number) => void;
  onPlanEndChange: (date: Dayjs | null) => void;
  onCommitmentDateChange: (date: Dayjs | null) => void;
  onPlanStartChange: (date: Dayjs | null) => void;
  onSenderAdded: (existingSenders: INumber[], sender: string) => void;
  onSenderRemoved: (sender: string) => void;
  onTimezoneCityChange: (city: SubscriptionTimezoneCity) => void;
  onTimezoneContinentChange: (continent: SubscriptionTimezoneContinent) => void;
  onTimezoneCountryChange: (country: SubscriptionTimezoneCountry) => void;
  onDataSourceChange: (datasource: SubscriptionDataSource) => void;
  onNetworkChange: (network: string) => void;
  onNotesChange: (notes: string) => void;
  onToggleScenario: (scenarioId: string) => void;
  onEmployeeInChargeChange: (employeeInChargeUid: string) => void;
  terminationDate: Dayjs | null;
  onTerminationDateChange: (date: Dayjs | null) => void;
  terminateSubscription: () => Promise<void>;
  resetUpdateStatus: () => void;
  onLeedbucksRatesChange: (rates: Partial<Record<CostTypeRef, { unit_value: number }>>) => void;
  onLeedbucksRatesTempChange: (periods: ILeedbucksRatesTemp[]) => void;
  onLeedbucksCreditsTempChange: (periods: ILeedbucksCreditsTemp[]) => void;
}

const SubscriptionEditionFormContext = createContext<SubscriptionEditionFormContextProps>({
  wipSubscription: null as Subscription | null,
  savedSubscription: null as Subscription | null,
  sendersHaveChanged: false,
  updateStatus: ContextStatus.IDLE,
  deleteStatus: ContextStatus.IDLE,
  partnerStatus: ContextStatus.IDLE,
  partnerSettingsUserUuids: [] as ISmallUser[],
  customProspectTypesStatus: ContextStatus.IDLE,
  customProspectTypes: [] as ICustomProspectType[],
  initWithSubscription: (_: Subscription) => {},
  submitUpdate: async () => { return Promise.resolve(); },
  submitDelete: async () => { return Promise.resolve(); },
  fetchPartnerUserUuids: async () => { return Promise.resolve(); },
  fetchPartnerCustomProspectTypes: async () => { return Promise.resolve(); },
  onCreditsChange: (_: ECreditType, _2: number) => {},
  onClearCredit: (_: ECreditType) => {},
  onResetCredits: (_: ECreditAdjustmentReason, _2: string | null) => { return Promise.resolve(false); },
  onAddCredits: (_: number, _2: ECreditAdjustmentReason, _3: string | null) => { return Promise.resolve(false); },
  onSetCredits: (_: number, _2: ECreditAdjustmentReason, _3: string | null) => { return Promise.resolve(false); },
  onCreditsAnchorDayChange: (_: number) => { return Promise.resolve(); },
  onCancelPlanChange: (_: string) => { return Promise.resolve(); },
  onChangePlan: (_: string, _2: string, _3: string | null) => { return Promise.resolve(false); },
  onCancelScheduledPlanChange: () => { return Promise.resolve(); },
  onNameChange: (_: string) => {},
  // CRMs Specific ---
  onSubscriptionSettingsEmailNotificationCopyChange: (_: string[]) => {},
  onSubscriptionSettingsBetaAccountChange: (_: boolean) => {},
  onSubscriptionSettingsDemoAccountChange: (_: boolean) => {},
  onSubscriptionSettingsPaygEnabledChange: (_: boolean) => {},
  onSubscriptionSettingsAutoArchiveTasksDaysChange: (_: number) => {},
  onSubscriptionSettingsPaymentWallChange: (_: boolean) => {},
  subscriptionStartEmail: async (_: any, aiFullName: string) => { return Promise.resolve(); },
  onPartnerSettingsImportModeChange: (_: string) => {},
  onPartnerSettingsProspectsSeniorityChange: (_: string) => {},
  onPartnerSettingsProspectsSeniorityMaxChange: (_: string) => {},
  onPartnerSettingsIncludeBuyersWithoutIdentifiedSearchChange: (_: boolean) => {},
  onPartnerSettingsIncludeSellersWithoutEstimationChange: (_: boolean) => {},
  onPartnerSettingsIncludeContactsWithoutTypologyChange: (_: boolean) => {},
  onPartnerSettingsArchiveProspectsWithoutInterestChange: (_: boolean) => {},
  onPartnerSettingsIncludeArchivedChange: (_: boolean) => {},
  onPartnerSettingsMaxCreditConsumptionPerMonthChange: (_: number) => {},
  onPartnerSettingsProspectsTypologiesChange: (_: string[]) => {},
  onPartnerSettingsProspectsTypologiesToScenariosChange: (_: string, _2: string) => {},
  onPartnerSettingsUserUuidsChange: (_: string[]) => {},
  // --- End CRMs Specific
  onIdPlanChange: (_: string) => {},
  onOpeningDaysChange: (_: SubscriptionOpeningDays) => {},
  onOpeningHoursStartChange: (_: number) => {},
  onOpeningHoursEndChange: (_: number) => {},
  onPlanEndChange: (_: Dayjs | null) => {},
  onCommitmentDateChange: (_: Dayjs | null) => {},
  onPlanStartChange: (_: Dayjs | null) => {},
  onSenderAdded: (existingSenders: INumber[], _: string) => {},
  onSenderRemoved: (_: string) => {},
  onTimezoneCityChange: (_: SubscriptionTimezoneCity) => {},
  onTimezoneContinentChange: (_: SubscriptionTimezoneContinent) => {},
  onTimezoneCountryChange: (_: SubscriptionTimezoneCountry) => {},
  onDataSourceChange: (_: SubscriptionDataSource) => {},
  onNetworkChange: (_: string) => {},
  onNotesChange: (_: string) => {},
  // Scenarios Specific ---
  onToggleScenario: (_: string) => {},
  // --- End Scenarios Specific
  onEmployeeInChargeChange: (_: string) => {},
  // Termination Specific ---
  terminationDate: null as Dayjs | null,
  onTerminationDateChange: (_: Dayjs | null) => {},
  terminateSubscription: async () => { return Promise.resolve(); },
  // --- End Termination Specific
  resetUpdateStatus: () => {},
  onLeedbucksRatesChange: (_: Partial<Record<CostTypeRef, { unit_value: number }>>) => {},
  onLeedbucksRatesTempChange: (_: ILeedbucksRatesTemp[]) => {},
  onLeedbucksCreditsTempChange: (_: ILeedbucksCreditsTemp[]) => {},
});

export const SubscriptionEditionFormProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [wipSubscription, setWipSubscription] = useState(null as Subscription | null);
  const [savedSubscription, setSavedSubscription] = useState(null as Subscription | null);

  const [updateStatus, setUpdateStatus] = useState(ContextStatus.IDLE);
  const [deleteStatus, setDeleteStatus] = useState(ContextStatus.IDLE);
  const [partnerStatus, setPartnerStatus] = useState(ContextStatus.IDLE);
  const [sendersHaveChanged, setSendersHaveChanged] = useState(false);
  const [terminationDate, setTerminationDate] = useState<Dayjs | null>(null);
  const [partnerSettingsUserUuids, setPartnerSettingsUserUuids] = useState([] as ISmallUser[]);
  const [customProspectTypesStatus, setCustomProspectTypesStatus] = useState(ContextStatus.IDLE);
  const [customProspectTypes, setCustomProspectTypes] = useState<ICustomProspectType[]>([]);
  const [clearedCredits, setClearedCredits] = useState<ECreditType[]>([]);

  const authContext = useContext(AuthenticationContext);

  const initWithSubscription = async (subscription: Subscription) => {
    setWipSubscription(subscription);
    setSavedSubscription(subscription);
    setClearedCredits([]);
  };

  const submitUpdate = async () => {
    if (wipSubscription === null) return;
    setUpdateStatus(ContextStatus.LOADING);

    try {
      let draftSubscription = wipSubscription.copyWith({});
      // Build prospects_typologies_to_scenarios object (remove orphan scenarios)
      const typologies = draftSubscription.partnerSettings.prospects_typologies || [];
      const typologiesToScenarios = typologies.map((typology) => ({
        [typology]: draftSubscription.partnerSettings.prospects_typologies_to_scenarios?.[typology],
      }));

      draftSubscription = draftSubscription.copyWith({
        partnerSettings: {
          ...draftSubscription.partnerSettings,
          prospects_typologies_to_scenarios: Object.assign({}, ...typologiesToScenarios),
        } as SubscriptionPartnerSettings,
      });

      // Clean prospects_typologies_to_scenarios to remove all undefined values in the map
      draftSubscription = draftSubscription.copyWith({
        partnerSettings: {
          ...draftSubscription.partnerSettings,
          prospects_typologies_to_scenarios: Object.entries(
            draftSubscription.partnerSettings.prospects_typologies_to_scenarios || {}
          )
            .filter(([k, v]) => v)
            .reduce((acc, [k, v]) => {
              acc[k] = v;
              return acc;
            }, {} as { [key: string]: string }),
        },
      });
      await updateSubscription(draftSubscription, { clearedCredits, keepPlan: draftSubscription.idPlan === savedSubscription?.idPlan });
      setClearedCredits([]);
      setWipSubscription(draftSubscription);
      setSavedSubscription(draftSubscription);
      setUpdateStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setUpdateStatus(ContextStatus.ERROR);
    }
  };

  const submitDelete = async () => {
    if (wipSubscription === null || authContext.authentication == null) return;
    setDeleteStatus(ContextStatus.LOADING);
    try {
      await deleteSubscription(wipSubscription!, authContext.authentication!);
      setDeleteStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setDeleteStatus(ContextStatus.ERROR);
    }
  };

  const fetchPartnerUserUuids = async () => {
    if (wipSubscription === null || authContext.authentication == null) return;
    setPartnerStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getPartnerUserUuids(
        wipSubscription.id,
        authContext.authentication
      );
      setPartnerSettingsUserUuids(useCaseResponse);
      setPartnerStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setPartnerStatus(ContextStatus.ERROR);
    }
  };

  const fetchPartnerCustomProspectTypes = async () => {
    if (wipSubscription === null || authContext.authentication == null) return;
    setCustomProspectTypesStatus(ContextStatus.LOADING);
    try {
      const oProspectTypes = [];
      switch (wipSubscription.partnerSystem) {
        case PartnersType.orisha:
          oProspectTypes.push(
            ...(await getOrishaCustomProspectTypes(wipSubscription, authContext.authentication))
              .groups
          );
          break;
      }
      setCustomProspectTypes(oProspectTypes);
      setCustomProspectTypesStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setCustomProspectTypesStatus(ContextStatus.ERROR);
    }
  };

  const onCreditsChange = (type: ECreditType, value: number) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        credits: { ...wipSubscription.credits, [type]: value },
      })
    );
  };

  const onClearCredit = (type: ECreditType) => {
    if (wipSubscription === null) return;
    const { [type]: _removed, ...rest } = wipSubscription.credits;
    setWipSubscription(wipSubscription.copyWith({ credits: rest as typeof wipSubscription.credits }));
    setClearedCredits((prev) => (prev.includes(type) ? prev : [...prev, type]));
  };
  
  const onResetCredits = async (reason: ECreditAdjustmentReason, comment: string | null) => {
    if (wipSubscription === null || authContext.authentication == null) return false;

    setUpdateStatus(ContextStatus.LOADING);
    try {
      const reset = await resetSubscriptionCreditsUseCase(
        wipSubscription.id,
        reason,
        comment,
        authContext.authentication
      );

      setWipSubscription(wipSubscription.copyWith({ credits: reset.credits }));
      setUpdateStatus(ContextStatus.SUCCESS);
      message.success('Crédits réinitialisés selon le plan');
      return true;
    } catch (e) {
      setUpdateStatus(ContextStatus.ERROR);
      message.error(
        e instanceof ResetSubscriptionCreditsFailure
          ? e.message
          : 'Erreur lors de la réinitialisation des crédits'
      );
      console.error(e);
      return false;
    }
  };

  const onAddCredits = async (amount: number, reason: ECreditAdjustmentReason, comment: string | null) => {
    if (wipSubscription === null || authContext.authentication == null) return false;

    setUpdateStatus(ContextStatus.LOADING);
    try {
      const added = await addSubscriptionCreditsUseCase(
        wipSubscription.id,
        amount,
        reason,
        comment,
        authContext.authentication
      );

      setWipSubscription(wipSubscription.copyWith({ credits: added.credits }));
      setUpdateStatus(ContextStatus.SUCCESS);
      message.success(`${amount} crédits ajoutés`);
      return true;
    } catch (e) {
      setUpdateStatus(ContextStatus.ERROR);
      message.error(
        e instanceof AddSubscriptionCreditsFailure
          ? e.message
          : "Erreur lors de l'ajout des crédits"
      );
      console.error(e);
      return false;
    }
  };

  const onSetCredits = async (leedbucks: number, reason: ECreditAdjustmentReason, comment: string | null) => {
    if (wipSubscription === null || authContext.authentication == null) return false;

    setUpdateStatus(ContextStatus.LOADING);
    try {
      const updated = await updateSubscriptionCreditsUseCase(
        wipSubscription.id,
        leedbucks,
        reason,
        comment,
        authContext.authentication
      );

      setWipSubscription(wipSubscription.copyWith({ credits: updated.credits }));
      setUpdateStatus(ContextStatus.SUCCESS);
      message.success('Solde de crédits modifié');
      return true;
    } catch (e) {
      setUpdateStatus(ContextStatus.ERROR);
      message.error(
        e instanceof UpdateSubscriptionCreditsFailure
          ? e.message
          : 'Erreur lors de la modification du solde'
      );
      console.error(e);
      return false;
    }
  };

  const onCreditsAnchorDayChange = async (day: number) => {
    if (wipSubscription === null || authContext.authentication == null) return;

    setUpdateStatus(ContextStatus.LOADING);
    try {
      const moved = await updateCreditsAnchorDayUseCase(
        wipSubscription.id,
        day,
        authContext.authentication
      );

      setWipSubscription(wipSubscription.copyWith({
        planStart: dayjs(moved.plan_start),
        creditsResetAt: moved.credits_reset_at ?? undefined,
        creditsPeriodEnd: moved.credits_period_end ?? undefined,
      }));
      setUpdateStatus(ContextStatus.SUCCESS);
      message.success(`Recharge déplacée au ${day} de chaque mois, prise en compte à 23h`);
    } catch (e) {
      setUpdateStatus(ContextStatus.ERROR);
      message.error(
        e instanceof UpdateCreditsAnchorDayFailure
          ? e.message
          : 'Erreur lors du changement de jour de recharge'
      );
      console.error(e);
    }
  };
  
  const onCancelPlanChange = async (companyId: string) => {
    if (wipSubscription === null || authContext.authentication == null) return;

    setUpdateStatus(ContextStatus.LOADING);
    try {
      await cancelSubscriptionPlanChangeUseCase(
        wipSubscription.id,
        companyId,
        authContext.authentication
      );

      setUpdateStatus(ContextStatus.SUCCESS);
      message.success('Downsell planifié annulé, le prix Stripe est remis sur le plan en cours');
    } catch (e) {
      setUpdateStatus(ContextStatus.ERROR);
      message.error(
        e instanceof CancelSubscriptionPlanChangeFailure
          ? e.message
          : "Erreur lors de l'annulation du downsell planifié"
      );
      console.error(e);
    }
  };

  const onChangePlan = async (companyId: string, newPlanId: string, scheduledDate: string | null) => {
    if (wipSubscription === null || authContext.authentication == null) return false;

    setUpdateStatus(ContextStatus.LOADING);
    try {
      const result = await changeSubscriptionPlanUseCase(
        wipSubscription.id,
        companyId,
        newPlanId,
        scheduledDate,
        authContext.authentication
      );

      // An upgrade switches the plan now: the form follows, or the next save would put the old one back
      if (result.change_type === 'upgrade') {
        setWipSubscription(wipSubscription.copyWith({ idPlan: newPlanId }));
        setSavedSubscription(savedSubscription?.copyWith({ idPlan: newPlanId }) ?? null);
      }

      setUpdateStatus(ContextStatus.SUCCESS);
      if (scheduledDate !== null) {
        message.success(`Changement de plan planifié le ${dayjs(scheduledDate).format('DD/MM/YYYY')}`);
      } else if (result.change_type === 'upgrade') {
        message.success('Plan changé, le prorata est facturé sur Stripe');
      } else {
        message.success(`Downsell planifié au ${dayjs(result.effective_date).format('DD/MM/YYYY')}, le prix Stripe est déjà positionné`);
      }
      return true;
    } catch (e) {
      setUpdateStatus(ContextStatus.ERROR);
      message.error(
        e instanceof ChangeSubscriptionPlanFailure
          ? e.message
          : 'Erreur lors du changement de plan'
      );
      console.error(e);
      return false;
    }
  };

  const onCancelScheduledPlanChange = async () => {
    if (wipSubscription === null || authContext.authentication == null) return;

    setUpdateStatus(ContextStatus.LOADING);
    try {
      await cancelScheduledPlanChangeUseCase(wipSubscription.id, authContext.authentication);

      setUpdateStatus(ContextStatus.SUCCESS);
      message.success('Changement de plan planifié annulé');
    } catch (e) {
      setUpdateStatus(ContextStatus.ERROR);
      message.error(
        e instanceof CancelScheduledPlanChangeFailure
          ? e.message
          : "Erreur lors de l'annulation du changement de plan planifié"
      );
      console.error(e);
    }
  };

    const onNameChange = (name: string) => {
      if (wipSubscription === null) return;
      setWipSubscription(wipSubscription.copyWith({ name }));
    };

  const onSubscriptionSettingsEmailNotificationCopyChange = (copies: string[]) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        settings: {
          ...wipSubscription.settings,
          email_notification_copy: copies,
        } as SubscriptionSettings,
      })
    );
  };

  const onSubscriptionSettingsBetaAccountChange = (beta_account: boolean) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        settings: {
          ...wipSubscription.settings,
          beta_account,
        } as SubscriptionSettings,
      })
    );
  };

  const onSubscriptionSettingsDemoAccountChange = (demo_account: boolean) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        settings: {
          ...wipSubscription.settings,
          demo_account,
        } as SubscriptionSettings,
      })
    );
  };

  const onSubscriptionSettingsPaygEnabledChange = (payg_enabled: boolean) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        settings: {
          ...wipSubscription.settings,
          payg_enabled,
        } as SubscriptionSettings,
      })
    );
  };

  const onSubscriptionSettingsAutoArchiveTasksDaysChange = (tasks_auto_archive_days: number) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        settings: {
          ...wipSubscription.settings,
          tasks_auto_archive_days,
        } as SubscriptionSettings,
      })
    );
  };

  const onSubscriptionSettingsPaymentWallChange = (payment_wall: boolean) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        settings: {
          ...wipSubscription.settings,
          payment_wall,
        } as SubscriptionSettings,
      })
    );
  };

  const onPartnerSettingsImportModeChange = (import_mode: string) => {
    if (wipSubscription === null) return;
    let partnerSettings = wipSubscription.partnerSettings;

    if (import_mode === 'auto' || import_mode === 'manual') {
      partnerSettings = {
        ...partnerSettings,
        prospects_typologies: [],
        users_uuids: [],
        prospects_seniority: '90_days',
        prospects_seniority_max: '1095_days',
        include_buyers_without_identified_search: true,
        include_sellers_without_estimation: true,
        include_contacts_without_typology: false,
        include_archived: false,
        max_credit_consumption_per_month: 100,
      } as SubscriptionPartnerSettings;
    }

    setWipSubscription(
      wipSubscription.copyWith({
        partnerSettings: {
          ...partnerSettings,
          import_mode,
        } as SubscriptionPartnerSettings,
      })
    );
  };

  const onPartnerSettingsProspectsSeniorityChange = (prospects_seniority: string) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        partnerSettings: {
          ...wipSubscription.partnerSettings,
          prospects_seniority,
        } as SubscriptionPartnerSettings,
      })
    );
  };

  const onPartnerSettingsProspectsSeniorityMaxChange = (prospects_seniority_max: string) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        partnerSettings: {
          ...wipSubscription.partnerSettings,
          prospects_seniority_max,
        } as SubscriptionPartnerSettings,
      })
    );
  };

  const onPartnerSettingsProspectsTypologiesChange = (prospects_typologies: string[]) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        partnerSettings: {
          ...wipSubscription.partnerSettings,
          prospects_typologies,
        } as SubscriptionPartnerSettings,
      })
    );
  };

  const onPartnerSettingsProspectsTypologiesToScenariosChange = (
    prospects_typology: string,
    scenarioId: string
  ) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        partnerSettings: {
          ...wipSubscription.partnerSettings,
          prospects_typologies_to_scenarios: {
            ...wipSubscription.partnerSettings.prospects_typologies_to_scenarios,
            [prospects_typology]: scenarioId,
          },
        } as SubscriptionPartnerSettings,
      })
    );
  };

  const onPartnerSettingsUserUuidsChange = (users_uuids: string[]) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        partnerSettings: {
          ...wipSubscription.partnerSettings,
          users_uuids,
        } as SubscriptionPartnerSettings,
      })
    );
  };

  const onPartnerSettingsIncludeBuyersWithoutIdentifiedSearchChange = (include_buyers_without_identified_search: boolean) => {
    if (wipSubscription === null || ![SubscriptionDataSource.septeo, SubscriptionDataSource.ac3].includes(wipSubscription.datasource as SubscriptionDataSource)) return;
    setWipSubscription(
      wipSubscription.copyWith({
        partnerSettings: {
          ...wipSubscription.partnerSettings,
          include_buyers_without_identified_search,
        } as SubscriptionPartnerSettings,
      })
    );
  }

  const onPartnerSettingsIncludeSellersWithoutEstimationChange = (include_sellers_without_estimation: boolean) => {
    if (wipSubscription === null || ![SubscriptionDataSource.septeo, SubscriptionDataSource.ac3].includes(wipSubscription.datasource as SubscriptionDataSource)) return;
    setWipSubscription(
      wipSubscription.copyWith({
        partnerSettings: {
          ...wipSubscription.partnerSettings,
          include_sellers_without_estimation,
        } as SubscriptionPartnerSettings,
      })
    );
  }

  const onPartnerSettingsIncludeContactsWithoutTypologyChange = (include_contacts_without_typology: boolean) => {
    if (wipSubscription === null || ![SubscriptionDataSource.septeo, SubscriptionDataSource.ac3].includes(wipSubscription.datasource as SubscriptionDataSource)) return;
    setWipSubscription(
      wipSubscription.copyWith({
        partnerSettings: {
          ...wipSubscription.partnerSettings,
          include_contacts_without_typology,
        } as SubscriptionPartnerSettings,
      })
    );
  }
            
  const onPartnerSettingsArchiveProspectsWithoutInterestChange = (archive_prospects_without_interest: boolean) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        partnerSettings: {
          ...wipSubscription.partnerSettings,
          archive_prospects_without_interest,
        } as SubscriptionPartnerSettings,
      })
    );
  }
            
  const onPartnerSettingsIncludeArchivedChange = (include_archived: boolean) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        partnerSettings: {
          ...wipSubscription.partnerSettings,
          include_archived,
        } as SubscriptionPartnerSettings,
      })
    );
  };

  const onPartnerSettingsMaxCreditConsumptionPerMonthChange = (max_credit_consumption_per_month: number) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        partnerSettings: {
          ...wipSubscription.partnerSettings,
          max_credit_consumption_per_month,
        } as SubscriptionPartnerSettings,
      })
    );
  };

  const subscriptionStartEmail = async (subscriptionsContext: any, aiFullName: string) => {
    if (!authContext.authentication) {
      console.error('Authentication context is null or undefined.');
      return;
    }

    try {
      const subscriptionUsers = subscriptionsContext.subscriptionUsers.filter(
        (user: { _type: string }) => user._type === 'subscription'
      );

      const emails = subscriptionUsers.map((user: { _email: string }) => user._email);
      // const emails = ["axel@leedpulse.com"] // test emails

      if (emails.length === 0) {
        message.warning("Aucun utilisateur avec le type 'subscription' trouvé.");
        return;
      }

      await sendStartEmail(
        emails,
        aiFullName,
        authContext.authentication,
        wipSubscription?.apiKey || ''
      );

      message.success(`Emails envoyés avec succès à : ${emails.join(', ')}`);
    } catch (error) {
      message.error("Erreur générale lors de l'envoi des emails.");
      console.error('Erreur :', error);
    }
  };

  const onIdPlanChange = (idPlan: string) => {
    if (wipSubscription === null) return;
    setWipSubscription(wipSubscription.copyWith({ idPlan }));
  };

  const onOpeningDaysChange = (openingDays: SubscriptionOpeningDays) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        opening: wipSubscription.opening.copyWith({ days: openingDays }),
      })
    );
  };

  const onOpeningHoursStartChange = (hours: number) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        opening: wipSubscription.opening.copyWith({
          hours: wipSubscription.opening.hours.copyWith({ start: hours }),
        }),
      })
    );
  };

  const onOpeningHoursEndChange = (hours: number) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        opening: wipSubscription.opening.copyWith({
          hours: wipSubscription.opening.hours.copyWith({ end: hours }),
        }),
      })
    );
  };

  const onPlanStartChange = (date: Dayjs | null) => {
    if (!date || wipSubscription === null) return;

    if (wipSubscription.planEnd && date.isAfter(wipSubscription.planEnd)) {
      message.error('La date de début ne peut pas être après la date de fin.');
      return;
    }

    setWipSubscription(wipSubscription.copyWith({ planStart: date }));
  };

  const onPlanEndChange = (date: Dayjs | null) => {
    if (!date || wipSubscription === null) return;

    if (wipSubscription.planStart && date.isBefore(wipSubscription.planStart)) {
      message.error('La date de fin ne peut pas être avant la date de début.');
      return;
    }

    setWipSubscription(wipSubscription.copyWith({ planEnd: date }));
  };

  const onCommitmentDateChange = (date: Dayjs | null) => {
    if (wipSubscription === null) return;

    setWipSubscription(wipSubscription.copyWith({ commitmentDate: date ? date.startOf('day').valueOf() : null }));
  };

  const onSenderAdded = (existingSenders: INumber[], sender: string) => {
    if (wipSubscription === null) return;
    if (wipSubscription.sender.includes(sender) || sender.trim() === '') return;
    setSendersHaveChanged(true);
    setWipSubscription(
      wipSubscription.copyWith({
        sender: [...wipSubscription.sender, sender.trim()],
      })
    );
  };

  const onSenderRemoved = (sender: string) => {
    if (wipSubscription === null) return;

    const updatedSender = wipSubscription.sender.filter((id) => id !== sender);
    setSendersHaveChanged(true);
    setWipSubscription(wipSubscription.copyWith({ sender: updatedSender }));
  };

  const onTimezoneCityChange = (city: SubscriptionTimezoneCity) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        timezone: wipSubscription.timezone.copyWith({ city }),
      })
    );
  };

  const onTimezoneContinentChange = (continent: SubscriptionTimezoneContinent) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        timezone: wipSubscription.timezone.copyWith({ continent }),
      })
    );
  };

  const onTimezoneCountryChange = (country: SubscriptionTimezoneCountry) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        timezone: wipSubscription.timezone.copyWith({ country }),
      })
    );
  };

  const onToggleScenario = (scenarioId: string) => {
    if (wipSubscription === null) return;
    const idScenarios = wipSubscription.idScenarios || [];
    if (idScenarios.includes(scenarioId)) {
      setWipSubscription(
        wipSubscription.copyWith({
          idScenarios: idScenarios.filter((id) => id !== scenarioId),
        })
      );
    } else {
      setWipSubscription(
        wipSubscription.copyWith({
          idScenarios: [...idScenarios, scenarioId],
        })
      );
    }
  };

  const onEmployeeInChargeChange = (employeeInChargeUid: string) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        employeeInChargeUid,
      })
    );
  };

  const onDataSourceChange = (datasource: SubscriptionDataSource) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        datasource,
      })
    );
  };

  const onNetworkChange = (network: string) => {
    if (wipSubscription === null) return;
    setWipSubscription(
      wipSubscription.copyWith({
        network,
      })
    );
  };

  const onNotesChange = (notes: string) => {
    if (wipSubscription === null) return;
    setWipSubscription(wipSubscription.copyWith({ notes }));
  };

  // Termination functions
  const onTerminationDateChange = (date: Dayjs | null) => {
    setTerminationDate(date);
  };

  const terminateSubscription = async () => {
    if (wipSubscription === null || authContext.authentication == null) return;

    setUpdateStatus(ContextStatus.LOADING);
    try {
      // Everything is done server-side (Stripe for Leedflow, payment wall & credits
      // for Leedpulse): we only refresh the form with the values it returns.
      const terminated = await terminateSubscriptionUseCase(
        {
          subscription_id: wipSubscription.id,
          termination_date: terminationDate?.valueOf(),
        },
        authContext.authentication
      );

      setWipSubscription(
        wipSubscription.copyWith({
          status: terminated.status,
          planEnd: dayjs(terminated.plan_end),
          notes: terminated.notes,
          credits: terminated.credits,
          sender: terminated.sender,
          settings: {
            ...wipSubscription.settings,
            payment_wall: terminated.payment_wall,
          } as SubscriptionSettings,
        })
      );
      setUpdateStatus(ContextStatus.SUCCESS);
      message.success('Abonnement résilié avec succès');
    } catch (e) {
      setUpdateStatus(ContextStatus.ERROR);
      message.error(
        e instanceof TerminateSubscriptionFailure
          ? e.message
          : "Erreur lors de la résiliation de l'abonnement"
      );
      console.error(e);
    }
  };

  const resetUpdateStatus = () => {
    setUpdateStatus(ContextStatus.IDLE);
  };

  const onLeedbucksRatesChange = (rates: Partial<Record<CostTypeRef, { unit_value: number }>>) => {
    if (wipSubscription === null) return;
    setWipSubscription(wipSubscription.copyWith({ leedbucksRates: rates }));
  };

  const onLeedbucksRatesTempChange = (periods: ILeedbucksRatesTemp[]) => {
    if (wipSubscription === null) return;
    setWipSubscription(wipSubscription.copyWith({ leedbucksRatesTemp: periods }));
  };

  const onLeedbucksCreditsTempChange = (periods: ILeedbucksCreditsTemp[]) => {
    if (wipSubscription === null) return;
    setWipSubscription(wipSubscription.copyWith({ leedbucksCreditsTemp: periods }));
  };

  return (
    <SubscriptionEditionFormContext.Provider
      value={{
        wipSubscription,
        savedSubscription,
        sendersHaveChanged,
        updateStatus,
        deleteStatus,
        partnerStatus,
        partnerSettingsUserUuids,
        customProspectTypesStatus,
        customProspectTypes,
        initWithSubscription,
        submitUpdate,
        submitDelete,
        fetchPartnerUserUuids,
        fetchPartnerCustomProspectTypes,
        onCreditsChange,
        onClearCredit,
        onNameChange,
        onResetCredits,
        onAddCredits,
        onSetCredits,
        onCreditsAnchorDayChange,
        onCancelPlanChange,
        onChangePlan,
        onCancelScheduledPlanChange,
        onIdPlanChange,
        onOpeningDaysChange,
        onOpeningHoursStartChange,
        onOpeningHoursEndChange,
        onSubscriptionSettingsEmailNotificationCopyChange,
        onSubscriptionSettingsBetaAccountChange,
        onSubscriptionSettingsDemoAccountChange,
        onSubscriptionSettingsPaygEnabledChange,
        onSubscriptionSettingsAutoArchiveTasksDaysChange,
        onSubscriptionSettingsPaymentWallChange,
        onDataSourceChange,
        onNetworkChange,
        onNotesChange,
        onPlanEndChange,
        onCommitmentDateChange,
        onPlanStartChange,
        onSenderAdded,
        onSenderRemoved,
        onTimezoneCityChange,
        onTimezoneContinentChange,
        onTimezoneCountryChange,
        onToggleScenario,
        // CRMs Specific ---
        subscriptionStartEmail,
        onPartnerSettingsImportModeChange,
        onPartnerSettingsProspectsSeniorityChange,
        onPartnerSettingsProspectsSeniorityMaxChange,
        onPartnerSettingsIncludeBuyersWithoutIdentifiedSearchChange,
        onPartnerSettingsArchiveProspectsWithoutInterestChange,
        onPartnerSettingsIncludeSellersWithoutEstimationChange,
        onPartnerSettingsIncludeContactsWithoutTypologyChange,
        onPartnerSettingsIncludeArchivedChange,
        onPartnerSettingsMaxCreditConsumptionPerMonthChange,
        onPartnerSettingsProspectsTypologiesChange,
        onPartnerSettingsProspectsTypologiesToScenariosChange,
        onPartnerSettingsUserUuidsChange,
        // --- End CRMs Specific
        onEmployeeInChargeChange,
        // Termination Specific ---
        terminationDate,
        onTerminationDateChange,
        terminateSubscription,
        // --- End Termination Specific
        resetUpdateStatus,
        onLeedbucksRatesChange,
        onLeedbucksRatesTempChange,
        onLeedbucksCreditsTempChange,
      }}
    >
      {children}
    </SubscriptionEditionFormContext.Provider>
  );
};

export default SubscriptionEditionFormContext;
